import React, { useMemo } from 'react';
import {
  Box, Container, Paper, Stack, Typography, GlobalStyles, alpha, useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider, PaletteMode } from '@mui/material/styles';
import { School as SchoolIcon } from '@mui/icons-material';
import { toUploadUrl } from '../../../utils/uploadUrl';

/**
 * Public admissions pages (apply, status) live outside the portal, so they
 * carry their own look: a school-branded hero band and one elevated card,
 * with a theme scoped to the page. Light or dark follows the visitor's own
 * device setting — a parent never sees the portal's theme toggle.
 */

/** Brand constants for the hero band, which looks the same in both modes.
 *  Everything below the hero uses theme tokens so it follows the mode. */
export const publicBrand = {
  navy: '#1b2a5c',
  navyDeep: '#0f1a3d',
  accent: '#d4a84b',
} as const;

// Inter is vendored under public/fonts/pdf for the PDF renderer; the public
// pages load the same files — no CDN request.
const FONT_BASE = `${process.env.PUBLIC_URL || ''}/fonts/pdf`;
const fontFaces = [400, 500, 600, 700].map((weight) => ({
  '@font-face': {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontDisplay: 'swap',
    fontWeight: weight,
    src: `url(${FONT_BASE}/Inter-${{ 400: 'Regular', 500: 'Medium', 600: 'SemiBold', 700: 'Bold' }[weight]}.ttf) format('truetype')`,
  },
}));

const tokens = {
  light: {
    primary: publicBrand.navy, primaryDark: publicBrand.navyDeep, contrast: '#fff',
    canvas: '#f4f6fb', paper: '#ffffff', field: '#ffffff',
    ink: '#0f172a', muted: '#5b6478', line: '#e3e7ef', lineHover: '#c3cadb',
  },
  dark: {
    // A lifted periwinkle keeps the brand blue legible on dark surfaces.
    primary: '#93a8ff', primaryDark: '#7189f0', contrast: '#0b1124',
    canvas: '#0a0f1f', paper: '#121a31', field: alpha('#ffffff', 0.03),
    ink: '#e8ebf4', muted: '#9aa4bd', line: alpha('#ffffff', 0.09), lineHover: alpha('#ffffff', 0.2),
  },
} as const;

export const makePublicTheme = (mode: PaletteMode) => {
  const t = tokens[mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: t.primary, dark: t.primaryDark, contrastText: t.contrast },
      background: { default: t.canvas, paper: t.paper },
      text: { primary: t.ink, secondary: t.muted },
      divider: t.line,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.field,
            transition: 'box-shadow 120ms ease',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.lineHover },
            '&.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(t.primary, mode === 'dark' ? 0.16 : 0.08)}` },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { fontWeight: 500 } } },
      MuiButton: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    },
  });
};

export const resolveSchoolLogo = (logo?: string | null): string | undefined => {
  if (!logo) return undefined;
  if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
  if (logo.startsWith('/')) return toUploadUrl(logo);
  return toUploadUrl('logos', logo);
};

export interface HeroFact { label: string; value: string; highlight?: boolean }

interface Props {
  school?: { name: string; logo?: string | null };
  /** Small line above the title ("Admissions open"). */
  eyebrow?: string;
  title?: string;
  description?: string;
  facts?: HeroFact[];
  /** Hero actions (e.g. prospectus download), top-right. */
  actions?: React.ReactNode;
  /** Card width — wide for forms, narrow for single messages. */
  width?: 'form' | 'narrow';
  children: React.ReactNode;
}

const PublicShell: React.FC<Props> = ({
  school, eyebrow, title, description, facts, actions, width = 'form', children,
}) => {
  const logo = resolveSchoolLogo(school?.logo);
  const mode: PaletteMode = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';
  const theme = useMemo(() => makePublicTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      {/* Native controls (date pickers, scrollbars) follow this page's mode,
          not the portal's. */}
      <GlobalStyles styles={[...fontFaces, { html: { colorScheme: mode } }] as any} />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', fontFamily: theme.typography.fontFamily }}>
        {/* ── Hero band ─────────────────────────────────────────────── */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            color: '#fff',
            pb: { xs: 12, md: 14 },
            background: `
              radial-gradient(900px 420px at 85% -10%, ${alpha('#5b7bd5', 0.45)}, transparent 60%),
              radial-gradient(700px 380px at -5% 110%, ${alpha(publicBrand.accent, 0.18)}, transparent 60%),
              linear-gradient(160deg, ${publicBrand.navy} 0%, ${publicBrand.navyDeep} 100%)`,
            // A faint fine grid for texture.
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: `linear-gradient(${alpha('#fff', 0.04)} 1px, transparent 1px), linear-gradient(90deg, ${alpha('#fff', 0.04)} 1px, transparent 1px)`,
              backgroundSize: '32px 32px',
              maskImage: 'linear-gradient(to bottom, black, transparent 85%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
            {/* School identity */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pt: { xs: 3, md: 4 } }}>
              <Box
                sx={{
                  width: 44, height: 44, borderRadius: '50%', bgcolor: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 0 4px ${alpha('#fff', 0.12)}`, overflow: 'hidden', flexShrink: 0,
                }}
              >
                {logo
                  ? <Box component="img" src={logo} alt="" sx={{ width: '86%', height: '86%', objectFit: 'contain' }} />
                  : <SchoolIcon sx={{ color: publicBrand.navy }} />}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', letterSpacing: 0.2, flex: 1 }}>
                {school?.name || 'Admissions'}
              </Typography>
              {actions}
            </Stack>

            {title && (
              <Box sx={{ mt: { xs: 5, md: 7 }, maxWidth: 640 }}>
                {eyebrow && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: publicBrand.accent }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, letterSpacing: 1.6, textTransform: 'uppercase', color: alpha('#fff', 0.8) }}>
                      {eyebrow}
                    </Typography>
                  </Stack>
                )}
                <Typography component="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                  {title}
                </Typography>
                {description && (
                  <Typography sx={{ mt: 1.5, fontSize: '1.02rem', lineHeight: 1.6, color: alpha('#fff', 0.78) }}>
                    {description}
                  </Typography>
                )}
              </Box>
            )}

            {facts && facts.length > 0 && (
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.25} sx={{ mt: 3.5 }}>
                {facts.map((f) => (
                  <Box
                    key={f.label}
                    sx={{
                      px: 1.75, py: 1, borderRadius: '12px',
                      bgcolor: f.highlight ? alpha(publicBrand.accent, 0.18) : alpha('#fff', 0.08),
                      border: `1px solid ${f.highlight ? alpha(publicBrand.accent, 0.45) : alpha('#fff', 0.14)}`,
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 0.6, textTransform: 'uppercase', color: alpha('#fff', 0.65), fontWeight: 600 }}>
                      {f.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mt: 0.25 }}>{f.value}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Container>
        </Box>

        {/* ── Card overlapping the hero ─────────────────────────────── */}
        <Container maxWidth={width === 'form' ? 'md' : 'sm'} sx={{ mt: { xs: -9, md: -10 }, position: 'relative', zIndex: 2, pb: 6 }}>
          <Paper
            elevation={0}
            sx={{
              // No overflow clipping: the apply form's sticky submit bar
              // needs to stick, which an overflow-hidden ancestor prevents.
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: mode === 'dark'
                ? '0 24px 48px -12px rgba(0,0,0,0.6)'
                : '0 1px 2px rgba(15,23,42,0.04), 0 24px 48px -12px rgba(15,23,42,0.14)',
            }}
          >
            {children}
          </Paper>
          <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.78rem', color: 'text.secondary' }}>
            Your information is sent securely to {school?.name || 'the school'} · Powered by EduBook
          </Typography>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PublicShell;
